import React, { useState, useEffect } from 'react';
import { getAccessToken } from '../api';
import './FileUpload.css';

const FileUpload = ({ onFileUploaded, accept = "*/*", fieldName = "" }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Cleanup object URL on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setFile(file);
    setUploadedUrl(null);
    setPreviewUrl(null);

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/files/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      setUploadedUrl(data.url);
      onFileUploaded(data.url);
      
      // Clean up local preview URL after successful upload
      if (previewUrl) {
        setTimeout(() => {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }, 1000); // Small delay to avoid flicker
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setError(error.message || 'Failed to upload file. Please try again.');
      // Clean up preview URL on error
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const getAcceptDescription = (accept) => {
    switch (accept) {
      case 'image/*': return 'Images (PNG, JPEG, WebP, etc.)';
      case 'video/*': return 'Videos (MP4, WebM, etc.)';
      case 'audio/*': return 'Audio files (MP3, WAV, etc.)';
      default:
        if (accept && accept !== '*/*') {
          return `Specific file types (${accept})`;
        }
        return 'Any file type';
    }
  };

  return (
    <div className="file-upload">
      <input 
        type="file" 
        onChange={handleUpload}
        accept={accept}
        disabled={loading}
        className="file-input"
      />
      <div className="file-accept-info">
        Accepts: {getAcceptDescription(accept)}
      </div>
      
      {loading && <div className="loading">Uploading...</div>}
      {error && <div className="error">{error}</div>}
      
      {file && !loading && !error && (
        <div className="file-info">
          {/* Show image preview on the left */}
          {(previewUrl || uploadedUrl) && accept.includes('image') && (
            <div className="image-preview">
              <img 
                src={previewUrl || uploadedUrl} 
                alt={previewUrl ? "Preview" : "Uploaded"} 
                className="preview-thumbnail"
                onLoad={() => {
                  // Clean up the object URL after the image loads
                  // We keep it until upload completes to avoid flicker
                }}
              />
            </div>
          )}
          
          <div className="file-details">
            <span>Selected file: {file.name}</span>
            {uploadedUrl && <span className="upload-success">✓ Uploaded successfully</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload; 